/**
 * Beta Request Page - Request Access to SmartPrompt v2
 * Public page where users can submit beta access requests
 */

import React, { useState } from 'react';
import { MainLayout } from '../layouts';
import { CheckCircleIcon, SparklesIcon, BeakerIcon, RocketLaunchIcon } from '@heroicons/react/24/outline';

const USE_CASE_OPTIONS = [
  'Academic',
  'Product research',
  'Prompt engineering',
  'Writing',
  'Marketing',
  'Policy review',
  'Education',
  'Other'
] as const;

interface FormData {
  email: string;
  name: string;
  useCase: typeof USE_CASE_OPTIONS[number];
  additionalDetails: string;
}

interface FormErrors {
  email?: string;
  name?: string;
  useCase?: string;
}

export const BetaRequestPage: React.FC = () => {
  const [formData, setFormData] = useState<FormData>({
    email: '',
    name: '',
    useCase: 'Academic',
    additionalDetails: '',
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    } else if (formData.name.trim().length > 100) {
      newErrors.name = 'Name must be 100 characters or less';
    }

    // Use case validation
    if (!formData.useCase) {
      newErrors.useCase = 'Please select a use case';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch('/api/access/request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: formData.email.trim().toLowerCase(),
          name: formData.name.trim(),
          useCase: formData.useCase,
          additionalDetails: formData.additionalDetails.trim() || undefined,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to submit request');
      }

      // Set localStorage flag to track that user has submitted a request
      localStorage.setItem('sp_beta_request_submitted', 'true');
      console.log('[BetaRequestPage] Set localStorage flag sp_beta_request_submitted');

      setIsSuccess(true);
    } catch (error) {
      console.error('Failed to submit beta request:', error);
      setErrorMessage(error instanceof Error ? error.message : 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field: keyof FormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  if (isSuccess) {
    return (
      <MainLayout>
        <div className="min-h-[60vh] flex items-center justify-center px-6">
          <div className="max-w-2xl w-full text-center">
            <div className="mb-8">
              <CheckCircleIcon className="w-20 h-20 text-green-400 mx-auto mb-6" />
              <h1 className="text-4xl font-bold text-white mb-4">
                Request Submitted!
              </h1>
              <p className="text-xl text-slate-300 mb-6">
                Thanks for your interest in SmartPrompt v2 beta!
              </p>
            </div>

            <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-8 mb-8">
              <p className="text-slate-300 mb-4">
                We've received your beta access request and will review it soon.
              </p>
              <p className="text-slate-300 mb-4">
                You'll receive an email at:
              </p>
              <p className="text-lg font-medium text-cyan-400 mb-4">
                {formData.email}
              </p>
              <p className="text-slate-300">
                when you're approved to start testing our latest features.
              </p>
            </div>

            <div className="space-y-4">
              <a
                href="/login"
                className="inline-block px-6 py-3 bg-cyan-600 hover:bg-cyan-700 text-white font-medium rounded-lg transition-colors"
              >
                Continue to Sign In
              </a>
              <p className="text-sm text-slate-400">
                Or{' '}
                <a href="/" className="text-cyan-400 hover:text-cyan-300 underline">
                  return to homepage
                </a>
              </p>
            </div>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          {/* Hero Section with Branding */}
          <div className="text-center mb-16">
            <div className="mb-8">
              <img
                src="/logos/Smart%20Prompt%20Logo.png"
                alt="Smart Prompt"
                className="h-12 mx-auto mb-6"
              />
              <div className="inline-flex items-center gap-2 bg-cyan-500/10 border border-cyan-500/30 rounded-full px-4 py-2 mb-6">
                <SparklesIcon className="w-5 h-5 text-cyan-400" />
                <span className="text-sm font-medium text-cyan-400">Early Access Program</span>
              </div>
            </div>

            <h1 className="text-4xl lg:text-5xl font-bold text-white leading-tight mb-6">
              Join the SmartPrompt Beta
            </h1>
            <p className="text-xl text-slate-300 mb-8 max-w-3xl mx-auto leading-relaxed">
              Get early access to features that surface the strongest response for your needs across every model we compare
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 mb-16">
            {/* Left Column: Benefits */}
            <div className="space-y-8">
              <h2 className="text-2xl font-bold text-white mb-6">
                What You'll Get
              </h2>

              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-cyan-500/10 border border-cyan-500/30 rounded-lg flex items-center justify-center">
                      <BeakerIcon className="w-6 h-6 text-cyan-400" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-2">
                      Compare 6+ AI Models Instantly
                    </h3>
                    <p className="text-slate-300">
                      Run one prompt across GPT-4, Claude, Gemini, and more—get scored results with a clear winner in ~60 seconds
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-purple-500/10 border border-purple-500/30 rounded-lg flex items-center justify-center">
                      <SparklesIcon className="w-6 h-6 text-purple-400" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-2">
                      Advanced Performance Scoring
                    </h3>
                    <p className="text-slate-300">
                      Detailed analysis across accuracy, clarity, completeness, and more—know exactly which AI excels at what
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-green-500/10 border border-green-500/30 rounded-lg flex items-center justify-center">
                      <RocketLaunchIcon className="w-6 h-6 text-green-400" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-2">
                      Early Access to New Features
                    </h3>
                    <p className="text-slate-300">
                      Be the first to test enhanced comparison tools, saved prompts, and team collaboration features
                    </p>
                  </div>
                </div>

                <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-6 mt-8">
                  <p className="text-sm text-slate-300 mb-2">
                    <span className="font-semibold text-white">Perfect for:</span>
                  </p>
                  <div className="grid grid-cols-2 gap-2 text-sm text-slate-400">
                    <div>• Researchers</div>
                    <div>• Product Teams</div>
                    <div>• Content Creators</div>
                    <div>• Developers</div>
                    <div>• Marketers</div>
                    <div>• Educators</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column: Form */}
            <div>
              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-8">
                <h2 className="text-2xl font-bold text-white mb-6">
                  Request Access
                </h2>

                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Email */}
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-slate-300 mb-2">
                      Email Address *
                    </label>
                    <input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleChange('email', e.target.value)}
                      className={`w-full px-4 py-3 bg-slate-900 border ${
                        errors.email ? 'border-red-500' : 'border-slate-600'
                      } rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500`}
                      placeholder="your@email.com"
                      disabled={isSubmitting}
                    />
                    {errors.email && (
                      <p className="mt-2 text-sm text-red-400">{errors.email}</p>
                    )}
                  </div>

                  {/* Name */}
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-slate-300 mb-2">
                      Full Name *
                    </label>
                    <input
                      id="name"
                      type="text"
                      value={formData.name}
                      onChange={(e) => handleChange('name', e.target.value)}
                      className={`w-full px-4 py-3 bg-slate-900 border ${
                        errors.name ? 'border-red-500' : 'border-slate-600'
                      } rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500`}
                      placeholder="Your name"
                      maxLength={100}
                      disabled={isSubmitting}
                    />
                    {errors.name && (
                      <p className="mt-2 text-sm text-red-400">{errors.name}</p>
                    )}
                  </div>

                  {/* Use Case */}
                  <div>
                    <label htmlFor="useCase" className="block text-sm font-medium text-slate-300 mb-2">
                      Primary Use Case *
                    </label>
                    <select
                      id="useCase"
                      value={formData.useCase}
                      onChange={(e) => handleChange('useCase', e.target.value as typeof USE_CASE_OPTIONS[number])}
                      className={`w-full px-4 py-3 bg-slate-900 border ${
                        errors.useCase ? 'border-red-500' : 'border-slate-600'
                      } rounded-lg text-white focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500`}
                      disabled={isSubmitting}
                    >
                      {USE_CASE_OPTIONS.map(option => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                    {errors.useCase && (
                      <p className="mt-2 text-sm text-red-400">{errors.useCase}</p>
                    )}
                  </div>

                  {/* Additional Details */}
                  <div>
                    <label htmlFor="additionalDetails" className="block text-sm font-medium text-slate-300 mb-2">
                      Tell us more (optional)
                    </label>
                    <textarea
                      id="additionalDetails"
                      value={formData.additionalDetails}
                      onChange={(e) => handleChange('additionalDetails', e.target.value)}
                      rows={4}
                      maxLength={1000}
                      className="w-full px-4 py-3 bg-slate-900 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 resize-none"
                      placeholder="What do you plan to use SmartPrompt for? Any specific features you're interested in?"
                      disabled={isSubmitting}
                    />
                    <p className="mt-2 text-xs text-slate-500">
                      {formData.additionalDetails.length}/1000 characters
                    </p>
                  </div>

                  {/* Error Message */}
                  {errorMessage && (
                    <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                      <p className="text-sm text-red-400">{errorMessage}</p>
                    </div>
                  )}

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full px-6 py-4 bg-cyan-600 hover:bg-cyan-700 disabled:bg-slate-700 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors text-lg"
                  >
                    {isSubmitting ? 'Submitting...' : 'Request Beta Access'}
                  </button>

                  <p className="text-center text-sm text-slate-400">
                    Already have access?{' '}
                    <a href="/" className="text-cyan-400 hover:text-cyan-300 underline">
                      Sign In
                    </a>
                  </p>
                </form>
              </div>
            </div>
          </div>

          {/* Social Proof / Trust Section */}
          <div className="border-t border-slate-700 pt-12">
            <div className="text-center max-w-3xl mx-auto">
              <p className="text-slate-400 mb-4">
                Join researchers, developers, and teams using SmartPrompt to make better AI decisions
              </p>
              <div className="flex justify-center gap-8 text-sm text-slate-500">
                <div>
                  <div className="text-2xl font-bold text-white mb-1">6+</div>
                  <div>AI Models</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-white mb-1">60s</div>
                  <div>Comparison Time</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-white mb-1">7+</div>
                  <div>Scoring Metrics</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};
